import dynamic from 'next/dynamic';
import { HStack } from '@chakra-ui/react';
import { Post } from '@/types';
import Fields, { resolveFields } from '@/components/frontend/Fields';
import ArticleLayout, {
  FieldsLayout,
} from '@/components/frontend/ArticleLayout';
import Hero from '@/components/frontend/Hero';
import { splitFields } from './Page';
import ContentSection from '../frontend/ContentSection';

const Buttons = dynamic(() => import('@/components/frontend/Buttons'));

const splitListingsFields = (
  fields: Post['fields'],
): {
  basicFields: Post['fields'];
  sectionFields: Post['fields'];
  listingFields: Post['fields'];
} => {
  const { basicFields, sectionFields: allSectionFields } = splitFields(fields);

  /* splitFields splits fields into narrowly rendered content type of fields and section type of fields
   * section type of fields include our listingSection and their filter too, but we need to control their
   * rendering position on the UI.
   *
   * The reason for this is the basic content rendering, which is not by design, but forced due to SEO/robots aspect.
   * If there's no content on this page, how can robots find it? We call it the SEO-hover and it's not meant for humans, hence
   * it's not designed naturally by designers.
   */
  const { sectionFields, listingFields } = allSectionFields.reduce(
    (acc, { __name, ...field }) => {
      if (!__name) {
        return acc;
      }
      const key = ['mt/listing-section', 'mt/search-listings'].includes(__name)
        ? 'listingFields'
        : 'sectionFields';
      return {
        ...acc,
        [key]: [...acc[key], { ...field, __name }],
      };
    },
    {
      listingFields: [],
      sectionFields: [],
    },
  );

  return {
    basicFields,
    sectionFields,
    listingFields,
  };
};

/*
 * Possible IDEA/TODO to be evaluated:
 * Render ContentSection as first item after the hero, but give it order="1". Then implement order="0" to listingFields
 * and order="2" for the sectionFields. This to make the content seem more like article content in eyes of Google. No idea if this works
 * it could and hence be worth a try.
 */
const ListingsTemplate = ({
  fields,
  post_title: title,
  post_excerpt: leadText,
  featured_image,
}: Post) => {
  const [hero, contentFields] = resolveFields(fields);
  const { basicFields, sectionFields, listingFields } =
    splitListingsFields(contentFields);

  const { buttons, ...heroProps } = hero
    ? {
        title: hero.heading || title,
        leadText: hero.text || leadText,
        image: hero.image || featured_image,
        buttons: hero.buttons || null,
      }
    : { title, leadText, image: featured_image, buttons: null };

  return (
    <ArticleLayout>
      <Hero {...heroProps} variant="landing">
        {buttons?.length ? (
          <Buttons
            buttons={buttons}
            {...(buttons.length > 1 ? { as: HStack, flexWrap: 'wrap' } : null)}
          />
        ) : null}
      </Hero>
      <FieldsLayout sectionsLayout>
        <Fields fields={listingFields} />
        {basicFields?.length ? (
          /* We could use Box here with 84ch maxWidth and mx="auto". But the issue is WP and content input, which we cant fully control.
           99% sure it would work, but not 100% */
          <ContentSection
            display={[null, null, null, null, 'grid']}
            maxW={['84ch', null, null, null, '100%']}
          >
            <Fields fields={basicFields} />
          </ContentSection>
        ) : null}
        <Fields fields={sectionFields} />
      </FieldsLayout>
    </ArticleLayout>
  );
};

export default ListingsTemplate;
